import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {useLazyUnmountCancelToken} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {ClickEventHandler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FileSizeFormatter} from '../../../../../../srcCommon/formatters/fileSizeFormatter';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {UploadedAttachmentModel} from '../../../../../models/api/attachmentModel';
import {
  DownloadAttachmentOptions,
  DownloadAttachmentResult,
  PreviewAttachmentOptions,
} from '../../../../../services/browser';
import {AttachmentDragSource} from '../attachments/attachmentDragSource';
import {AttachmentFileIcon} from '../attachments/attachmentFileIcon';
import {CommentViewerSingleImageAttachmentPreview} from './commentViewerSingleImageAttachmentPreview';

/*
 * Props.
 */

interface CommentViewerSingleImageAttachmentProps {
  attachment: UploadedAttachmentModel;
  attachmentDownloadUrl: string;
  newMultiAttachmentStyle?: boolean;
  isInteractionDisabled?: boolean;
  onPreviewClickAsync: (options: PreviewAttachmentOptions) => Promise<void>;
  onDownloadClickAsync: (options: DownloadAttachmentOptions) => Promise<DownloadAttachmentResult>;
}

/*
 * Style.
 */

const StyledAttachmentDragSource = styled(AttachmentDragSource)`
  position: relative;
`;

interface OverlayStyleProps {
  $newMultiAttachmentStyle?: boolean;
}
const StyledOverlayDiv = styled.div<OverlayStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  padding: 7px 7px 7px 8px;

  border-radius: ${(p) => (p.$newMultiAttachmentStyle ? '6px' : '15px')};
  background-image: linear-gradient(
    -180deg,
    ${(p) => p.theme.alphas.white10} 0%,
    ${(p) => p.theme.alphas.white60} 100%
  );
  cursor: zoom-in;

  transition: opacity 300ms ease;
  opacity: 0;

  ${StyledAttachmentDragSource}:hover & {
    opacity: 1;
  }
`;

const StyledDetailsDiv = styled.div`
  display: grid;
  grid-template-areas: 'icon name size action';
  grid-template-columns: auto 1fr auto auto;
  grid-column-gap: 4px;
  align-items: center;

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  margin-right: 2px;
`;

const StyledNameDiv = styled.div`
  grid-area: name;
  ${ellipsis()};
`;

const StyledSizeDiv = styled.div`
  grid-area: size;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledActionDiv = styled.div`
  grid-area: action;
`;

const StyledActionButton = styled(ChromelessButton)`
  padding: 3px 3px 3px 2px;
`;

// The isolated prop is the button color.
const StyledActionButtonDiv = styled.div`
  padding: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};

  div:hover > && {
    background: ${(p) => p.theme.palette.blue.shade50};
  }
`;

/*
 * Component.
 */

export const CommentViewerSingleImageAttachment: FC<CommentViewerSingleImageAttachmentProps> = (props) => {
  const theme = useUserTheme();
  const {
    attachment,
    attachmentDownloadUrl,
    newMultiAttachmentStyle,
    isInteractionDisabled,
    onPreviewClickAsync,
    onDownloadClickAsync,
  } = props;
  const {visualContentType, name, size} = attachment;
  const buildUnmountCancelToken = useLazyUnmountCancelToken();

  /*
   * Event handlers.
   */

  const onPreviewClick: ClickEventHandler = async (event) => {
    if (event.defaultPrevented) {
      return;
    }

    try {
      await onPreviewClickAsync({cancelToken: buildUnmountCancelToken()});
    } catch (error) {
      reportToBugsnag(error);
    }
  };

  const onDownloadClick: ClickEventHandler = async (event) => {
    event.preventDefault();

    try {
      const shouldPromptForPath = event.shiftKey;
      await onDownloadClickAsync({cancelToken: buildUnmountCancelToken(), shouldPromptForPath});
    } catch (error) {
      reportToBugsnag(error);
    }
  };

  const renderImageDownloadOverlay = () => (
    <StyledOverlayDiv $newMultiAttachmentStyle={props.newMultiAttachmentStyle}>
      <StyledDetailsDiv>
        <StyledIconDiv>
          <AttachmentFileIcon contentType={visualContentType} />
        </StyledIconDiv>
        <StyledNameDiv>{name}</StyledNameDiv>
        <StyledSizeDiv>
          <FileSizeFormatter value={size} />
        </StyledSizeDiv>
        <StyledActionDiv>
          <StyledActionButton onClick={onDownloadClick}>
            <StyledActionButtonDiv>
              <Icon name="downloadTiny" color={theme.greys.white} />
              <VisuallyHidden>
                <Trans id="attachment-download">Download</Trans>
              </VisuallyHidden>
            </StyledActionButtonDiv>
          </StyledActionButton>
        </StyledActionDiv>
      </StyledDetailsDiv>
    </StyledOverlayDiv>
  );

  const renderImageContent = () => (
    <StyledAttachmentDragSource attachment={attachment}>
      <CommentViewerSingleImageAttachmentPreview
        src={attachmentDownloadUrl}
        alt={name}
        newMultiAttachmentStyle={newMultiAttachmentStyle}
      />
      {!isInteractionDisabled && renderImageDownloadOverlay()}
    </StyledAttachmentDragSource>
  );

  /*
   * Render.
   */

  return isInteractionDisabled ? (
    <div>{renderImageContent()}</div>
  ) : (
    <ChromelessButton onClick={onPreviewClick}>{renderImageContent()}</ChromelessButton>
  );
};
